import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { api, fullName, formatDate, formatDateTime, mensajeError } from '../lib/api';
import { normalizaTexto } from '../lib/utils';
import { PageHeader, EmptyState } from '../components/Common';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Eye, ShieldCheck, Trash2, Tablet, ExternalLink, Camera, Loader2, Sparkles, Link2, MessageCircle, Copy, Search } from 'lucide-react';
import { toast } from 'sonner';
import PdfPreviewDialog from '../components/PdfPreviewDialog';
import { PatientCombobox } from '../components/PatientCombobox';
import { avisoAltaConOrigen, emailValido, AVISO_EMAIL } from '../components/PatientFormDialog';
import CampoComoNosConocio from '../components/CampoComoNosConocio';
import { AVISO_FALTA_ORIGEN, faltaOrigen } from '../lib/origenes';
import { avisarSaludoNeutro } from '../lib/citasApi';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const lopdCasaLink = (token) => `${window.location.origin}/firma-lopd?casa=1&t=${encodeURIComponent(token)}`;

/**
 * EL ENLACE QUE SE COPIA ES EL QUE COMPONE EL SERVIDOR (dirección, 2026-09-21).
 *
 * Aquí se reconstruía el enlace a partir del token y se tiraba el `&cita=`
 * que el servidor acababa de calcular y devolver en `enlace`. Resultado: el
 * mismo paciente, el mismo endpoint y la misma pulsación de recepción daban
 * DOS enlaces distintos, el bueno por WhatsApp (ahí se abre el `whatsapp_url`
 * del servidor) y uno mutilado si se copiaba.
 *
 * La cita es lo ÚNICO que decide si el formulario le pedirá al paciente las
 * dos fotos (documents_router._pide_fotos_del_documento_casa). Sin ella se
 * piden siempre y el envío se bloquea: la paciente de revisión sucesiva a la
 * que recepción le pega el enlace copiado en un WhatsApp escrito a mano se
 * queda delante de un formulario que no puede terminar. Es la queja de
 * Administración del 15/9/2026, viva por la otra puerta — y el canario diario no la
 * ve, porque comprobar_enlaces.py abre siempre el enlace pelado.
 *
 * `lopdCasaLink(token)` queda de plan B y solo para un servidor antiguo que
 * todavía no devuelva `enlace`.
 */
const enlaceDeCasa = (envio) => envio?.enlace || lopdCasaLink(envio?.token || '');

/* ---------------------------------------------------------------------------
 * BUSCADOR DE LA LISTA DE LOPD (administración, 17/9/2026)
 * -------------------------------------------------
 * Con sus palabras: «que se añada un buscador que permita localizar una LOPD
 * en concreto […] por nombre, apellido o número de teléfono», sin que castigue
 * por tildes, por mayúsculas ni por cómo esté registrado el paciente.
 *
 * QUIÉN BUSCA: el SERVIDOR (GET /api/lopd?q=…), sobre la colección ENTERA.
 * Nació filtrando aquí lo que ya se había bajado, y así no podía salir bien:
 * la lista de LOPD viaja recortada (las más recientes) y el teléfono ni
 * siquiera venía en ella —estaba en las 200 fichas de alta más recientes—, así
 * que el caso más normal de la clínica (paciente antiguo que firma la LOPD
 * esta semana) aparecía en la tabla pero NO se encontraba por su teléfono: se
 * tecleaba el número que cantaba el paciente y la pantalla contestaba
 * «Ninguna LOPD coincide» con la firma dos líneas más abajo. De ese «no
 * coincide» falso se sale haciéndole firmar otra vez o reteniéndole antes de
 * un procedimiento.
 *
 * LA REGLA DE AQUÍ ABAJO sigue viva, pero solo para adelantar trabajo mientras
 * la respuesta del servidor está en camino: filtra lo que YA hay en pantalla
 * (el teléfono viene ahora en cada registro, `patient_phone`). En cuanto el
 * servidor contesta a lo que está escrito manda él y esto no filtra nada. Y
 * mientras no haya contestado, la pantalla NO dice que algo no exista: dice
 * que está buscando.
 * ------------------------------------------------------------------------- */

const soloCifras = (v) => String(v || '').replace(/[^0-9]/g, '');

/** Un móvil español se busca igual con el 34 delante que sin él. */
const sin34 = (cifras) => (cifras.startsWith('34') && cifras.length === 11 ? cifras.slice(2) : cifras);

/**
 * ¿Lo que se ha escrito es ESTE teléfono, escrito de cualquiera de sus formas?
 *
 * El teléfono se PINTA con prefijo y huecos («+34 600 12 34 56») pero puede
 * estar GUARDADO de cualquier manera, así que quien copia lo que ve en la
 * pantalla y lo pega aquí no encontraría a nadie y parecería que el paciente
 * no existe (el mismo fallo que se arregló en la búsqueda global el
 * 2026-09-15). Se comparan solo las cifras, por los dos lados.
 *
 * Seis cifras de mínimo, igual que `_es_el_telefono` en
 * backend/routers/search_router.py: por debajo de eso nadie está buscando un
 * teléfono y comparar sin separadores haría coincidir a media clínica.
 */
export function esElTelefono(texto, phone) {
  const qd = soloCifras(texto);
  if (qd.length < 6) return false;
  const pd = soloCifras(phone);
  return !!pd && (sin34(pd).includes(sin34(qd)) || pd.includes(qd));
}

/**
 * El móvil de una LOPD (administración, 29/10/2026: «el paciente introduce su
 * móvil en la tablet y al consultar la LOPD no aparece»): el que escribió al
 * firmar (`datos_firmados.phone`) y, en las de antes, el de su ficha.
 */
export function telefonoDeLaLopd(registro) {
  return String(registro?.datos_firmados?.phone || registro?.patient_phone || '').trim();
}

/**
 * Todo lo que identifica una fila, junto y normalizado: el nombre que guardó
 * la LOPD y el teléfono del paciente, que desde hoy viaja en el propio
 * registro (`patient_phone`), también en cifras sueltas.
 */
function blancoDeBusqueda(registro) {
  return normalizaTexto([
    registro?.patient_name,
    registro?.patient_phone, soloCifras(registro?.patient_phone),
    registro?.datos_firmados?.phone, soloCifras(registro?.datos_firmados?.phone),
  ].filter(Boolean).join(' '));
}

/**
 * ¿Entra esta LOPD en lo que se está buscando? Sin texto, todas.
 *
 * Por tokens en CUALQUIER orden, como el buscador del servidor
 * (backend/services/search_utils.py): «fer mari» encuentra a «María
 * Fernández», y un apellido suelto basta. Aparte va el teléfono ENTERO,
 * porque quien pega «+34 600 12 34 56» escribe cuatro trozos que por separado
 * no son nada.
 *
 * El teléfono sale del REGISTRO, no de una lista de fichas aparte: mientras se
 * sacaba de las 200 fichas más recientes, a un paciente antiguo que acababa de
 * firmar no se le encontraba por su número. Esto adelanta el filtro mientras
 * el servidor contesta; quien decide de verdad es él.
 */
export function coincideLopd(registro, texto) {
  const q = (texto || '').trim();
  if (!q) return true;
  if (esElTelefono(q, registro?.patient_phone)) return true;
  if (esElTelefono(q, registro?.datos_firmados?.phone)) return true;
  const blob = blancoDeBusqueda(registro);
  return normalizaTexto(q).split(/\s+/).filter(Boolean).every((t) => blob.includes(t));
}

/**
 * Tope que se le pide al servidor. Lo pide la PANTALLA, para poder decir la
 * verdad cuando se llega a él («se muestran las 500 primeras») en vez de
 * enseñar un recorte como si fuera todo.
 */
const LIMITE = 500;

// Etiqueta de la columna "Origen": distingue quién firmó y dónde. Lo que no
// esté aquí es un registro que creó recepción a mano, sin firma.
const ORIGEN_LOPD = {
  tablet_signed: { texto: 'Tablet · firmado', clase: 'bg-[rgba(46,110,94,0.18)] text-[#13231E]' },
  enlace_casa_signed: { texto: 'Enlace · firmado', clase: 'bg-[rgba(46,110,94,0.18)] text-[#13231E]' },
};
const ORIGEN_MANUAL = { texto: 'Manual', clase: 'bg-[rgba(222,222,222,0.5)] text-[rgba(11,18,32,0.7)]' };

/**
 * LA LOPD FIRMADA NO SE BORRA (23/9/2026). El servidor contesta 409 al
 * borrarla (`backend/services/documentos_firmados.py::lopd_firmada`), y esta
 * es SU regla, copiada tal cual para saber qué fila lleva papelera: firmada =
 * lleva el trazo del paciente, o viene de una puerta donde firma él (tablet o
 * enlace de casa) aunque el trazo no esté. En duda, el papel se queda. La
 * «Manual» sin trazo no guarda ninguna firma y se sigue pudiendo quitar. Un
 * «trazo» de solo espacios no es trazo: el servidor hace strip() y aquí trim()
 * (los dos quitan espacios, saltos de línea y tabuladores).
 *
 * Antes la papelera salía en todas las filas: en las firmadas era un botón
 * que siempre falla. Si algún día cambia la regla del servidor, cambia aquí
 * también (o, en el peor caso, la papelera sale y el 409 se enseña: ver
 * `onDelete`).
 */
const ORIGENES_LOPD_FIRMADA = ['tablet_signed', 'enlace_casa_signed'];
export function lopdFirmada(registro) {
  return Boolean(String(registro?.signature_b64 || '').trim())
    || ORIGENES_LOPD_FIRMADA.includes(registro?.source);
}

// Cómo se rehace una LOPD firmada, con los nombres de los botones que ya hay
// en esta pantalla. Es la misma salida que da el servidor en su 409: no hace
// falta borrar nada, la nueva queda la primera y esta se conserva.
const REHACER_LOPD = 'Esta LOPD está firmada por el paciente y no se borra: se conserva tal cual se firmó. '
  + 'Para rehacerla (datos o permisos que cambian), que firme una nueva en «Modo tablet» o desde '
  + 'el «Enlace para casa»: la nueva queda la primera y esta se conserva.';

/**
 * Abre el WhatsApp del enlace de casa con lo que devuelve el servidor
 * (2026-09-05, dirección: «pásalo a formal igual»). El texto lo redacta el
 * backend, de usted y con «Estimada/Estimado» según el género de la ficha,
 * que el navegador no puede saber; si sale «Estimado/a», el mismo aviso que
 * el resto de la app.
 *
 * Devuelve '' si se abrió y, si no, EL MOTIVO (dirección, 2026-09-21). Antes
 * devolvía false y las dos puertas lo traducían a «Este paciente no tiene
 * teléfono», que con «677889900 (madre 2)» escrito en la ficha —diez cifras
 * sin país: no se sabe a qué país mandarlo— es sencillamente falso, y la
 * propia pastilla PINTA ese teléfono al lado del botón. Recepción leía que no
 * lo había, abría la ficha, lo veía escrito y concluía que el programa estaba
 * roto, sin que nadie supiera qué corregir. El motivo lo redacta el servidor
 * con la regla de la casa (services/telefonos.py), igual que en el Agente.
 */
function motivoDelWhatsAppQueNoSale(envio) {
  if (!envio) return 'No se pudo crear el enlace seguro';
  const tel = String(envio.telefono || '').trim();
  const motivo = String(envio.motivo || '').trim();
  if (!motivo) {
    // Servidor antiguo, que todavía no manda el motivo: se avisa sin
    // inventarse un dato. No se dice que no haya teléfono, porque no consta.
    return tel
      ? `No se puede mandar por WhatsApp al teléfono de la ficha «${tel}»`
      : 'No se puede mandar el enlace por WhatsApp';
  }
  const cabeza = tel
    ? `No se puede mandar el enlace por WhatsApp al teléfono de la ficha «${tel}»`
    : 'No se puede mandar el enlace por WhatsApp';
  return `${cabeza} — ${motivo}. Corrígelo en la ficha del paciente.`;
}

function abrirWhatsAppEnlaceCasa(envio, nombre) {
  if (!envio?.whatsapp_url) return motivoDelWhatsAppQueNoSale(envio);
  window.open(envio.whatsapp_url, '_blank', 'noopener,noreferrer');
  if (envio.saludo_neutro) avisarSaludoNeutro(nombre);
  return '';
}

/**
 * Enlace para casa de CUALQUIER paciente: se busca por nombre/NIF, se genera el
 * enlace seguro y se muestra escrito para poder copiarlo (o seleccionarlo a
 * mano si el navegador bloquea el portapapeles) y mandarlo por WhatsApp.
 * Antes solo se podía con los pacientes de la lista de pendientes.
 */
function HomeLinkDialog({ open, onOpenChange, paraPaciente = null }) {
  const [patient, setPatient] = useState(null);
  const [link, setLink] = useState('');
  // Respuesta del servidor con el WhatsApp ya redactado (mensaje, whatsapp_url, saludo_neutro).
  const [envio, setEnvio] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  /* LA LISTA DE PACIENTES NO LA PRECARGA ESTE DIÁLOGO (18/9/2026).
   *
   * Aquí había un `api.get('/api/patients')` con `.catch(() => {})` sobre un
   * estado que arrancaba en `[]`. Si la petición fallaba, el estado se quedaba
   * en `[]`... y `[]` es TRUTHY: el `PatientCombobox` entra por
   * `if (externalPatients) return;` (PatientCombobox.jsx), no hace su propia
   * carga, nunca llega a su `_ErrorState` y cae en la rama
   * `patients.length === 0`, que afirma «Sin pacientes registrados · Crea uno
   * primero desde Pacientes». Sin botón de reintentar.
   *
   * Es un fallo de red disfrazado de dato, y del que peor se sale: recepción
   * lee que el paciente no existe y vuelve a crear la ficha que ya estaba, con
   * su LOPD y sus cobros colgando de la nueva. Lo contrario de fusionar
   * duplicados.
   *
   * No se pasa el prop y punto: el combobox carga por su cuenta, trae su
   * «Error cargando pacientes» con su «Reintentar» —que existía y quedaba
   * inalcanzable— y además busca en el servidor mientras se teclea, que aquí
   * hacía falta (esta lista solo traía las fichas más recientes). */
  const onPick = useCallback(async (id, ficha) => {
    // La ficha la devuelve el propio combobox, que es quien la tiene: puede
    // venir de su búsqueda en servidor y no estar en ninguna lista precargada.
    setPatient(ficha || { id });
    setLink('');
    setEnvio(null);
    setBusy(true);
    try {
      const res = await api.post(`/api/lopd/patients/${id}/home-link`);
      setLink(enlaceDeCasa(res.data));
      setEnvio(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo crear el enlace seguro');
    } finally {
      setBusy(false);
    }
  }, []);

  /* «REHACER» TRAE YA AL PACIENTE DE SU FILA (2ª pasada, 23/9/2026). Antes
   * abría este diálogo vacío y recepción tenía que volver a buscar a quien
   * acababa de pulsar. Se hace lo mismo que al elegirlo en el selector: se
   * pide su enlace, que se queda en pantalla para copiarlo o mandarlo (no se
   * envía nada solo). Abierto desde el botón de arriba, sale vacío. */
  useEffect(() => {
    if (!open) return;
    setPatient(null); setLink(''); setEnvio(null); setBusy(false);
    if (paraPaciente?.id) onPick(paraPaciente.id, { id: paraPaciente.id, name: paraPaciente.nombre });
  }, [open, paraPaciente, onPick]);

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Enlace copiado');
    } catch {
      // Sin permiso de portapapeles: se selecciona para copiar a mano.
      inputRef.current?.select();
      toast.info('Selecciónalo y copia con Ctrl+C (en Mac, Cmd+C)');
    }
  };

  const porWhatsApp = () => {
    const fallo = abrirWhatsAppEnlaceCasa(envio, fullName(patient));
    if (fallo) toast.error(fallo);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Enlace para casa</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {/* Viene de «Rehacer» en una LOPD firmada, y sigue siendo ese
              paciente (si se elige otro en el selector, esto se va). */}
          {paraPaciente?.id && patient?.id === paraPaciente.id && (
            <div className="text-sm bg-[rgba(46,110,94,0.08)] text-[#13231E] rounded px-3 py-2" data-testid="lopd-home-link-rehacer">
              Rehacer la LOPD de <strong>{paraPaciente.nombre}</strong>: que la firme de nuevo con este enlace. La firmada se conserva.
            </div>
          )}
          <div>
            <Label>Paciente</Label>
            {/* El selector solo sabe poner nombre a las fichas que ha cargado
                (las más recientes): al paciente que llega de «Rehacer» se le
                nombra con lo que ya sabemos, en vez de «Selecciona paciente…»
                con el enlace de otro debajo. */}
            <PatientCombobox
              value={patient?.id || ''}
              onChange={onPick}
              placeholder={(patient && fullName(patient)) || 'Selecciona paciente…'}
              dataTestid="lopd-home-link-patient"
            />
          </div>
          {busy && <div className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Generando enlace seguro…</div>}
          {link && (
            <div className="space-y-2">
              <Label>Enlace (se usa una sola vez; no caduca mientras no lo rellenen)</Label>
              <Input ref={inputRef} readOnly value={link} onFocus={(e) => e.target.select()} data-testid="lopd-home-link-input" />
              <div className="flex gap-2">
                <Button onClick={copiar} variant="outline" className="flex-1" data-testid="lopd-home-link-copy">
                  <Copy className="w-4 h-4 mr-2" /> Copiar
                </Button>
                <Button onClick={porWhatsApp} disabled={!envio?.whatsapp_url} className="flex-1 bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="lopd-home-link-wa">
                  <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp
                </Button>
              </div>
              {/* Un botón apagado que no dice por qué es un error enseñado como
                  si fuera un dato (dirección, 2026-09-21): aquí se lee el
                  teléfono que hay escrito y qué le pasa, sin tener que pulsar
                  nada. El enlace de arriba sale igual y se puede copiar. */}
              {envio && !envio.whatsapp_url && (
                <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5" data-testid="lopd-home-link-sin-wa">
                  {motivoDelWhatsAppQueNoSale(envio)}
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewLopdDialog({ open, onOpenChange, onCreated, initialPatient, prefillData }) {
  const [patient_id, setPatientId] = useState(initialPatient || '');
  const [r, setR] = useState(false);
  const [h, setH] = useState(false);
  const [c, setC] = useState(false);
  // «¿Cómo nos conoció?» al cerrar la LOPD manual (decisión de dirección,
  // 23/9/2026: obligatorio). Sale con lo que ya tenga la ficha; si la ficha no
  // lo tiene, hay que elegirlo —o «No lo recuerda»— y se guarda en la ficha.
  // `null` = aún no se ha leído la ficha de este paciente.
  const [origen, setOrigen] = useState(null);
  const [origenTocado, setOrigenTocado] = useState(false);

  /* LA LISTA DE PACIENTES TAMPOCO SE PRECARGA AQUÍ (18/9/2026). El mismo caso
   * que en «Enlace para casa», dos funciones más arriba, y peor: esta petición
   * no tenía ni `catch`. El estado se quedaba en `[]` —truthy—, el
   * `PatientCombobox` no cargaba por su cuenta y el diálogo de «Generar LOPD»
   * afirmaba «Sin pacientes registrados» cuando lo que había fallado era la
   * petición. Se lo dejamos al combobox, que sabe decir que ha fallado. */
  useEffect(() => {
    if (open) {
      setPatientId(initialPatient || '');
      setR(false); setH(false); setC(false);
    }
  }, [open, initialPatient]);

  // El origen que ya tiene la ficha del paciente elegido. Si la lectura
  // falla se deja elegir igual: el servidor usa el de la ficha si lo hay.
  useEffect(() => {
    setOrigen(null);
    setOrigenTocado(false);
    if (!open || !patient_id) return undefined;
    let vivo = true;
    api.get(`/api/patients/${patient_id}`)
      .then((res) => { if (vivo) setOrigen(res.data?.how_known_us || ''); })
      .catch(() => { if (vivo) setOrigen(''); });
    return () => { vivo = false; };
  }, [open, patient_id]);

  const submit = async () => {
    if (!patient_id) return toast.error('Selecciona paciente');
    if (faltaOrigen(origen)) {
      setOrigenTocado(true);
      return toast.error(AVISO_FALTA_ORIGEN);
    }
    try {
      const res = await api.post('/api/lopd', {
        patient_id, consent_reminders: r, consent_health_info: h, consent_commercial: c,
        how_known_us: origen,
      });
      toast.success('LOPD creado');
      // Si la ficha se queda con otro origen («No lo recuerda» no pisa uno de
      // verdad), se dice en vez de callarlo (23/9/2026). El papel conserva lo
      // que se eligió aquí.
      if (res.data?.aviso_origen) toast.warning(res.data.aviso_origen, { duration: 10000 });
      onCreated && onCreated(res.data);
      onOpenChange(false);
    } catch (err) {
      toast.error(mensajeError(err, 'No se pudo crear la LOPD'));
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Generar LOPD</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Paciente</Label>
            <PatientCombobox value={patient_id} onChange={setPatientId} dataTestid="lopd-patient-select" />
          </div>
          {patient_id && origen !== null && (
            <CampoComoNosConocio
              valor={origen}
              onChange={setOrigen}
              onBlur={() => setOrigenTocado(true)}
              tocado={origenTocado}
              id="lopd-how-known-us"
              testid="lopd-how-known-us"
            />
          )}
          <div className="space-y-2 pt-2">
            <label className="flex items-center gap-2"><Checkbox checked={r} onCheckedChange={setR} data-testid="lopd-consent-reminders" /><span className="text-sm">Recordatorios de citas</span></label>
            <label className="flex items-center gap-2"><Checkbox checked={h} onCheckedChange={setH} data-testid="lopd-consent-health" /><span className="text-sm">Información de salud</span></label>
            <label className="flex items-center gap-2"><Checkbox checked={c} onCheckedChange={setC} data-testid="lopd-consent-commercial" /><span className="text-sm">Comunicaciones comerciales</span></label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={submit} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="lopd-create-button">Crear</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Diálogo de revisión tras extraer datos de una foto/PDF de LOPD.
 * El usuario edita los campos extraídos por la IA y al confirmar:
 *  1. Crea el paciente
 *  2. Crea la LOPD asociada con consents marcados
 *
 * Exige los MISMOS cuatro datos que la ficha de Pacientes (nombre, dos
 * apellidos y teléfono, administración 2026-09-11) y con el mismo aviso. Aquí hace
 * falta de verdad: lo que rellena los campos es la IA leyendo un papel, y si
 * no supo sacar un apellido antes esto dejaba pulsar «Crear paciente + LOPD»
 * para que el servidor contestara 400 con la LOPD sin crear.
 *
 * Se exporta para poder probarlo suelto, sin montar la página entera.
 */
export function ReviewExtractedDialog({ open, onOpenChange, extracted, onSaved }) {
  const empty = {
    name: '', surname1: '', surname2: '', nif: '', birth_date: '', email: '', phone: '',
    address: { calle: '', poblacion: '', provincia: '', codigo_postal: '' },
    profession: '', how_known_us: '', notes: '',
  };
  const [form, setForm] = useState(empty);
  const [consents, setConsents] = useState({ reminders: false, health: false, commercial: false });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && extracted) {
      setForm({
        ...empty,
        ...extracted,
        address: extracted.address || empty.address,
      });
      setConsents({
        reminders: !!extracted.consent_reminders,
        health: !!extracted.consent_health_info,
        commercial: !!extracted.consent_commercial,
      });
    }
    // eslint-disable-next-line
  }, [open, extracted]);

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setAddr = (k, v) => setForm(f => ({ ...f, address: { ...(f.address || {}), [k]: v } }));

  // Lo que falta para el alta (los cuatro y, desde el 23/9/2026, «cómo nos
  // conoció») o un email que no lo parece. Lo que haya leído la IA del papel
  // sale como valor actual del desplegable, aunque no sea del catálogo.
  const aviso = avisoAltaConOrigen(form) || (emailValido(form.email) ? '' : AVISO_EMAIL);

  const submit = async (e) => {
    e.preventDefault();
    if (aviso) return toast.error(aviso);
    setBusy(true);
    try {
      // 1) Crear paciente
      const patientRes = await api.post('/api/patients', form);
      const patient = patientRes.data;
      // 2) Crear LOPD asociado, con el origen elegido aquí: el papel imprime
      // lo que se eligió en él (23/9/2026).
      await api.post('/api/lopd', {
        patient_id: patient.id,
        consent_reminders: consents.reminders,
        consent_health_info: consents.health,
        consent_commercial: consents.commercial,
        how_known_us: form.how_known_us,
      });
      toast.success(`Paciente "${fullName(patient)}" creado con LOPD asociado`);
      onSaved && onSaved(patient);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al guardar');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#2E6E5E]" />
            Revisar datos extraídos del documento LOPD
          </DialogTitle>
        </DialogHeader>
        {extracted?.confidence && (
          <div className="text-xs bg-[rgba(46,110,94,0.08)] text-[#13231E] p-2 rounded mb-2">
            <strong>Confianza de extracción: {extracted.confidence}.</strong> Revisa los datos antes de confirmar. Al guardar se creará el paciente y su LOPD.
          </div>
        )}
        {/* noValidate: el aviso lo damos nosotros, entero y en cristiano. */}
        <form onSubmit={submit} noValidate className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div><Label>Nombre <span className="text-red-600">*</span></Label><Input required value={form.name} onChange={(e) => setField('name', e.target.value)} data-testid="ext-name" /></div>
            <div><Label>Primer apellido <span className="text-red-600">*</span></Label><Input required value={form.surname1 || ''} onChange={(e) => setField('surname1', e.target.value)} data-testid="ext-surname1" /></div>
            <div><Label>Segundo apellido <span className="text-red-600">*</span></Label><Input required value={form.surname2 || ''} onChange={(e) => setField('surname2', e.target.value)} data-testid="ext-surname2" /></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div><Label>NIF/NIE</Label><Input value={form.nif || ''} onChange={(e) => setField('nif', e.target.value)} data-testid="ext-nif" /></div>
            <div><Label>Fecha nacimiento</Label><Input type="date" value={form.birth_date || ''} onChange={(e) => setField('birth_date', e.target.value)} data-testid="ext-birth" /></div>
            <div><Label>Teléfono <span className="text-red-600">*</span></Label><Input required value={form.phone || ''} onChange={(e) => setField('phone', e.target.value)} data-testid="ext-phone" /></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><Label>Email</Label><Input type="email" value={form.email || ''} onChange={(e) => setField('email', e.target.value)} data-testid="ext-email" /></div>
            <div><Label>Profesión</Label><Input value={form.profession || ''} onChange={(e) => setField('profession', e.target.value)} /></div>
          </div>
          <CampoComoNosConocio
            valor={form.how_known_us}
            onChange={(v) => setField('how_known_us', v)}
            id="ext-how-known-us"
            testid="ext-how-known-us"
          />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="md:col-span-2"><Label>Calle</Label><Input value={form.address?.calle || ''} onChange={(e) => setAddr('calle', e.target.value)} /></div>
            <div><Label>Población</Label><Input value={form.address?.poblacion || ''} onChange={(e) => setAddr('poblacion', e.target.value)} /></div>
            <div><Label>CP</Label><Input value={form.address?.codigo_postal || ''} onChange={(e) => setAddr('codigo_postal', e.target.value)} /></div>
          </div>
          <div>
            <Label className="mb-2 block">Consentimientos marcados en el documento</Label>
            <div className="space-y-2 bg-[rgba(46,110,94,0.04)] p-3 rounded">
              <label className="flex items-center gap-2 text-sm"><Checkbox checked={consents.reminders} onCheckedChange={(v) => setConsents(s => ({ ...s, reminders: !!v }))} />Recordatorios de citas</label>
              <label className="flex items-center gap-2 text-sm"><Checkbox checked={consents.health} onCheckedChange={(v) => setConsents(s => ({ ...s, health: !!v }))} />Información de salud</label>
              <label className="flex items-center gap-2 text-sm"><Checkbox checked={consents.commercial} onCheckedChange={(v) => setConsents(s => ({ ...s, commercial: !!v }))} />Comunicaciones comerciales</label>
            </div>
          </div>
          {/* Qué falta, antes de pulsar: el botón está apagado y sin esto no
              se sabe por qué. */}
          {aviso && (
            <div className="text-xs text-red-600" data-testid="ext-faltan">{aviso}</div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancelar</Button>
            <Button type="submit" disabled={busy || !!aviso} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="ext-save">
              {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Guardando…</> : <><Plus className="w-4 h-4 mr-2" /> Crear paciente + LOPD</>}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------------------------------------------------------------------
 * INTENTOS QUE NO SALIERON (dirección, 30/10/2026)
 * -----------------------------------------------
 * Pacientes que dicen que la LOPD «les dio error», desde casa o en la tablet,
 * y nadie podía saber qué vieron. El servidor apunta cada «Firmar y enviar»
 * que no sale —los que rechaza él y los que la propia página frena o se
 * quedan sin respuesta— (backend services/lopd_intentos.py), y aquí se ven los
 * de los últimos 30 días: cuándo, desde dónde, el motivo que se le dio, el
 * paciente cuando se sabe (en la tablet no se sabe hasta que firma) y si
 * acabó firmando después, para llamarle y para saber si es un fallo del
 * programa o un dato mal puesto. Plegado: la sección es la lista de LOPD.
 * Si no se ha podido consultar, se dice; nunca un «ninguno» sin mirar.
 * ------------------------------------------------------------------------- */

const ORIGEN_INTENTO = { casa: 'Casa', tablet: 'Tablet' };
const TIPO_INTENTO = {
  validacion_cliente: 'La página no dejó enviar',
  sin_respuesta: 'Sin respuesta',
  error_servidor: 'Error de conexión o del servidor',
  no_se_sabe: 'No se pudo confirmar si llegó',
  error_pagina: 'Fallo de la página',
};

/** Qué fue, en pocas palabras, y el código HTTP si lo hubo. */
export function claseDeIntento(intento) {
  const que = intento?.fuente === 'pagina'
    ? (TIPO_INTENTO[intento?.tipo] || 'Aviso de la página')
    : 'Rechazado por el servidor';
  return intento?.codigo ? `${que} · ${intento.codigo}` : que;
}

/** ¿Firmó después? Sí / No / — (sin paciente no se sabe). */
export function textoFirmoDespues(intento) {
  if (intento?.firmo_despues === true) return 'Sí';
  if (intento?.firmo_despues === false) return 'No';
  return '—';
}

export function IntentosQueNoSalieron() {
  // `null` mientras no haya una respuesta buena.
  const [datos, setDatos] = useState(null);
  const [fallo, setFallo] = useState('');
  const [abierto, setAbierto] = useState(false);

  const cargar = useCallback(async () => {
    setFallo('');
    try {
      const r = await api.get('/api/lopd/intentos-fallidos');
      const d = r?.data && typeof r.data === 'object' && !Array.isArray(r.data) ? r.data : {};
      setDatos({
        intentos: Array.isArray(d.intentos) ? d.intentos : [],
        recortado: !!d.recortado,
        sinFirmar24h: Number.isInteger(d.sin_firmar_24h) ? d.sin_firmar_24h : 0,
      });
    } catch (err) {
      setDatos(null);
      setFallo(err?.response?.data?.detail || 'No se pudieron consultar');
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const n = datos ? datos.intentos.length : 0;
  const sinFirmar = datos ? datos.sinFirmar24h : 0;
  const resumen = fallo
    ? `${fallo}. Esto no quiere decir que no haya ninguno: vuelve a intentarlo.`
    : datos === null ? 'Comprobando…'
      : n === 0 ? 'Ninguno en los últimos 30 días.'
        : `${n}${datos.recortado ? '+' : ''} en los últimos 30 días.`;

  return (
    <div className="cjo-card p-4 mb-4" data-testid="lopd-intentos">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-sm font-semibold text-[#13231E]">Intentos que no salieron</div>
          <div className="text-xs text-muted-foreground mt-0.5" data-testid="lopd-intentos-resumen">{resumen}</div>
        </div>
        <div className="flex items-center gap-2">
          {sinFirmar > 0 && (
            <span
              className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-50 text-amber-900 border border-amber-200"
              title="Pacientes con un intento en las últimas 24 horas y sin LOPD firmada después"
              data-testid="lopd-intentos-aviso"
            >
              {sinFirmar} {sinFirmar === 1 ? 'paciente' : 'pacientes'} sin LOPD tras el fallo (24 h)
            </span>
          )}
          {fallo
            ? <Button size="sm" variant="outline" onClick={cargar} data-testid="lopd-intentos-reintentar">Reintentar</Button>
            : n > 0 && (
              <Button size="sm" variant="outline" onClick={() => setAbierto((v) => !v)} data-testid="lopd-intentos-ver">
                {abierto ? 'Ocultar' : 'Ver'}
              </Button>
            )}
        </div>
      </div>
      {abierto && n > 0 && (
        <div className="overflow-x-auto mt-3">
          <table className="w-full text-sm">
            <thead className="bg-[rgba(46,110,94,0.04)] text-left"><tr>
              <th className="py-2 px-3 font-semibold text-[#13231E]">Fecha</th>
              <th className="py-2 px-3 font-semibold text-[#13231E]">Dónde</th>
              <th className="py-2 px-3 font-semibold text-[#13231E]">Motivo</th>
              <th className="py-2 px-3 font-semibold text-[#13231E]">Paciente</th>
              <th className="py-2 px-3 font-semibold text-[#13231E]">¿Firmó después?</th>
            </tr></thead>
            <tbody>{datos.intentos.map((it) => (
              <tr key={it.id} className="border-t border-border align-top" data-testid="lopd-intento-row">
                <td className="py-2 px-3 text-xs whitespace-nowrap">{formatDateTime(it.fecha)}</td>
                <td className="py-2 px-3 text-xs">{ORIGEN_INTENTO[it.origen] || it.origen || '—'}</td>
                <td className="py-2 px-3">
                  <div className="text-xs">{it.motivo || '—'}</div>
                  <div className="text-[11px] text-muted-foreground">{claseDeIntento(it)}</div>
                </td>
                <td className="py-2 px-3 text-xs">
                  {it.paciente?.id
                    ? (
                      <Link to={`/pacientes/${it.paciente.id}`} className="text-[#2E6E5E] hover:underline" data-testid="lopd-intento-paciente">
                        {it.paciente.nombre || 'Ficha'}{it.paciente.num_historia != null ? ` · nº ${it.paciente.num_historia}` : ''}
                      </Link>
                    )
                    : <span className="text-muted-foreground" title={it.origen === 'tablet' ? 'En la tablet no se sabe quién es hasta que firma' : 'Sin ficha asociada'}>—</span>}
                </td>
                <td className="py-2 px-3 text-xs" data-testid="lopd-intento-firmo">{textoFirmoDespues(it)}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function LopdPage() {
  const [searchParams] = useSearchParams();
  const initialPatient = searchParams.get('patient');
  const [items, setItems] = useState([]);
  // Los pendientes de firma: `null` mientras no haya una respuesta buena, y si
  // no, `{ total, lista }`. Ha tenido dos cerraduras por el mismo camino:
  //   1. nació como `[]` con un `catch` vacío, así que una consulta fallida
  //      dejaba la cabecera diciendo «0 pacientes pendientes de firma»;
  //   2. y una vez tapado eso, el número seguía saliendo de `/api/patients`
  //      pedido sin parámetros, que devuelve las 200 fichas más recientes POR
  //      FECHA DE ALTA: la cuenta era «pendientes entre los 200 últimos en
  //      darse de alta», y una paciente de 2019 sin firmar volvía a dar «0».
  // En LOPD ese cero falso es dar por firmado a quien no ha firmado, así que
  // ahora la cuenta la da el servidor sobre la colección entera
  // (GET /api/lopd/pendientes) y el TOTAL viaja aparte de la lista, que llega
  // recortada a propósito (medido el 18/9/2026).
  const [pendientes, setPendientes] = useState(null);
  const [falloPendientes, setFalloPendientes] = useState('');
  // Lo que el usuario escribe en el buscador (administración, 17/9/2026).
  const [search, setSearch] = useState('');
  // Lo que el SERVIDOR ya ha contestado: qué texto buscó (null = todavía nada,
  // que es la primera carga) y si la respuesta venía tocando el tope.
  const [buscado, setBuscado] = useState(null);
  const [truncado, setTruncado] = useState(false);
  // Cuántas LOPD hay en total, de la última carga SIN buscar: es lo que se
  // enseña en la cabecera, que no puede encogerse al filtrar. `null` mientras
  // no haya salido bien ninguna carga: si la consulta falla, la cabecera decía
  // «0 registros» al lado de un «No se pudo consultar». Un cero que nadie ha
  // comprobado no es un dato (misma regla que los pendientes, 18/9/2026).
  const [total, setTotal] = useState(null);
  const [buscando, setBuscando] = useState(false);
  const [fallo, setFallo] = useState('');
  const [open, setOpen] = useState(!!initialPatient);
  const [preview, setPreview] = useState({ open: false, url: '', filename: '', title: '' });
  const [homeLinkBusy, setHomeLinkBusy] = useState('');
  const [homeLinkOpen, setHomeLinkOpen] = useState(false);
  // El paciente con el que se abre «Enlace para casa» desde «Rehacer» ({ id,
  // nombre }); null desde el botón de arriba, que sale vacío.
  const [homeLinkPara, setHomeLinkPara] = useState(null);

  // Upload + extract
  const fileInputRef = useRef(null);
  const [extractBusy, setExtractBusy] = useState(false);
  const [extracted, setExtracted] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);

  // Cuál es la última petición que se ha lanzado: si una vieja llega tarde, se
  // tira. Tecleando deprisa, la respuesta de «fer» no puede pisar la de
  // «fernandez» y enseñar filas que ya no casan con lo que se ve escrito.
  const peticion = useRef(0);

  const cargarLopd = useCallback(async (texto) => {
    const q = (texto || '').trim();
    const mia = peticion.current + 1;
    peticion.current = mia;
    setBuscando(true);
    // Se limpia el fallo anterior al empezar: si no, un reintento se quedaría
    // enseñando el error de la vez pasada mientras este va de camino.
    setFallo('');
    try {
      const r = await api.get('/api/lopd', { params: q ? { q, limit: LIMITE } : { limit: LIMITE } });
      if (peticion.current !== mia) return;
      const lista = r.data || [];
      setItems(lista);
      setTruncado(lista.length >= LIMITE);
      if (!q) setTotal({ n: lista.length, recortado: lista.length >= LIMITE });
      setBuscado(q);
      setFallo('');
    } catch (err) {
      if (peticion.current !== mia) return;
      // Sin respuesta no se sabe NADA: ni que hay coincidencias ni que no las
      // hay. Se dice que ha fallado, no que no exista.
      setFallo(err?.response?.data?.detail || 'No se pudo consultar la lista de LOPD');
    } finally {
      if (peticion.current === mia) setBuscando(false);
    }
  }, []);

  // El aviso de pendientes va por su cuenta: no depende de lo que se busque, y
  // si falla no puede llevarse por delante la lista de firmas. Pero tampoco
  // puede tragarse el fallo y quedarse en cero: sin respuesta no se sabe a
  // cuántos les falta firmar, así que se dice eso y no un número.
  //
  // QUIEN CUENTA ES EL SERVIDOR, sobre la colección entera. Contarlo aquí, a
  // partir de las fichas que caben en `/api/patients`, no contaba pendientes:
  // contaba pendientes entre los 200 últimos en darse de alta (ver el estado
  // `pendientes` arriba y backend/routers/documents_router.py::lopd_pendientes).
  const cargarPendientes = useCallback(async () => {
    setFalloPendientes('');
    try {
      const r = await api.get('/api/lopd/pendientes');
      const d = r.data || {};
      const lista = Array.isArray(d.pacientes) ? d.pacientes : [];
      // El TOTAL es el que manda y llega aparte: la lista viene recortada a
      // propósito (la pantalla solo pinta unas pocas pastillas). Si el número
      // se sacara de la lista, sería otra vez un recorte enseñado como si
      // fuera todo.
      setPendientes({ total: typeof d.total === 'number' ? d.total : lista.length, lista });
    } catch (err) {
      setPendientes(null);
      setFalloPendientes(err?.response?.data?.detail || 'No se pudo consultar quién está pendiente de firma');
    }
  }, []);

  useEffect(() => { cargarPendientes(); }, [cargarPendientes]);

  // Un cuarto de segundo desde la última tecla antes de preguntar, como en
  // Pacientes. Sin texto no se espera: es volver a la lista de siempre.
  useEffect(() => {
    const q = search.trim();
    if (!q) { cargarLopd(''); return undefined; }
    const t = setTimeout(() => cargarLopd(q), 250);
    return () => clearTimeout(t);
  }, [search, cargarLopd]);

  // Tras crear, borrar o importar: se vuelve a pedir todo, respetando lo que
  // haya escrito en el buscador.
  const refrescar = useCallback(() => {
    cargarLopd(search);
    cargarPendientes();
  }, [cargarLopd, cargarPendientes, search]);

  const q = search.trim();
  // ¿Lo que hay en pantalla es la respuesta del servidor a lo que está
  // escrito? Solo entonces se puede afirmar algo sobre lo que NO hay.
  const alDia = buscado === q && !buscando;
  const primeraCarga = buscado === null && !fallo;

  // La cabecera, con la misma regla en sus dos mitades: un número SOLO cuando
  // hay una respuesta buena; mientras se pregunta, que se está comprobando; si
  // ha fallado, que no se ha podido consultar. Nunca un cero prestado.
  const textoRegistros = total
    ? `${total.n}${total.recortado ? '+' : ''} registros`
    : fallo ? 'registros: no se han podido consultar' : 'registros: comprobando…';

  const textoPendientes = falloPendientes
    ? 'pendientes de firma: no se han podido consultar'
    : pendientes === null
      ? 'pendientes de firma: comprobando…'
      : `${pendientes.total} ${pendientes.total === 1 ? 'paciente pendiente' : 'pacientes pendientes'} de firma`;

  // Las pastillas que caben en el aviso rojo, y cuántos quedan detrás. Se
  // cuenta sobre el TOTAL del servidor, no sobre la lista recibida: la lista ya
  // viene recortada, y restarle 16 a un recorte daría un «+N más» que se queda
  // corto justo cuando más gente falta por firmar.
  const pastillas = pendientes ? pendientes.lista.slice(0, 16) : [];
  const masPendientes = pendientes ? pendientes.total - pastillas.length : 0;

  // Las filas que se ven. Cuando el servidor ya ha contestado a este texto,
  // manda él: filtrar encima escondería filas que él sí ha encontrado (por
  // NIF, por email…). Mientras contesta, se adelanta el filtro de aquí.
  const visibles = useMemo(
    () => (alDia ? items : items.filter(l => coincideLopd(l, q))),
    [items, q, alDia],
  );

  /**
   * Borrar SOLO lo que no lleva firma (23/9/2026). Antes no capturaba el
   * error: con el 409 del servidor ante una LOPD firmada no se enseñaba nada
   * y la promesa quedaba rechazada; recepción aceptaba el «¿Eliminar?» y la
   * pantalla se quedaba callada, como si no hubiera pasado nada. Ahora se
   * enseña el motivo que da el servidor (que ya dice cómo rehacerla) y, si ha
   * contestado, se vuelve a pedir la lista: la fila que se veía no era la de
   * verdad (la firmaron entre medias, o ya no estaba).
   */
  const onDelete = async (id) => {
    if (!window.confirm('¿Eliminar?')) return;
    try {
      await api.delete(`/api/lopd/${id}`);
      toast.success('Eliminado');
      refrescar();
    } catch (err) {
      toast.error(mensajeError(err, 'No se ha podido borrar la LOPD. Comprueba la conexión y vuelve a intentarlo.'));
      if (err?.response) refrescar();
    }
  };

  // En la firmada, en lugar de la papelera: cómo se rehace, y la acción que ya
  // existe para hacerlo desde aquí (el enlace para casa; la tablet está en su
  // botón de arriba y el aviso lo nombra).
  const rehacerLopd = (registro) => {
    toast.info(REHACER_LOPD, { duration: 10000 });
    setHomeLinkPara(registro?.patient_id
      ? { id: registro.patient_id, nombre: registro.patient_name || '' }
      : null);
    setHomeLinkOpen(true);
  };

  const onFileChosen = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setExtractBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/api/patients/extract-from-document', fd, { timeout: 120000 });
      setExtracted(res.data);
      setReviewOpen(true);
      toast.success(`Datos extraídos · confianza: ${res.data.confidence || 'media'}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error extrayendo datos');
    } finally {
      setExtractBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const getHomeLinkForPatient = async (patient) => {
    const res = await api.post(`/api/lopd/patients/${patient.id}/home-link`);
    return res.data;
  };

  const copyHomeLinkForPatient = async (patient) => {
    setHomeLinkBusy(patient.id);
    try {
      const envio = await getHomeLinkForPatient(patient);
      await navigator.clipboard.writeText(enlaceDeCasa(envio));
      toast.success(`Enlace seguro copiado para ${fullName(patient)}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo crear el enlace seguro');
    } finally {
      setHomeLinkBusy('');
    }
  };

  const sendHomeLinkByWhatsApp = async (patient) => {
    setHomeLinkBusy(patient.id);
    try {
      const envio = await getHomeLinkForPatient(patient);
      const fallo = abrirWhatsAppEnlaceCasa(envio, fullName(patient));
      if (fallo) toast.error(fallo);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo crear el enlace seguro');
    } finally {
      setHomeLinkBusy('');
    }
  };

  return (
    <div>
      <PageHeader
        title="LOPD"
        subtitle={`${textoRegistros} · ${textoPendientes}`}
        actions={<>
          <input ref={fileInputRef} type="file" accept="application/pdf,image/png,image/jpeg,image/webp,image/heic,image/heif" className="hidden" onChange={onFileChosen} data-testid="lopd-upload-input" />
          <Button onClick={() => fileInputRef.current?.click()} variant="outline" disabled={extractBusy} data-testid="lopd-upload-photo-button">
            {extractBusy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Extrayendo…</> : <><Camera className="w-4 h-4 mr-2" /> Subir foto LOPD</>}
          </Button>
          <Button onClick={() => window.open('/firma-lopd', '_blank')} variant="outline" data-testid="lopd-tablet-mode-button">
            <Tablet className="w-4 h-4 mr-2" /> Modo tablet <ExternalLink className="w-3 h-3 ml-1.5 opacity-60" />
          </Button>
          <Button
            onClick={() => { setHomeLinkPara(null); setHomeLinkOpen(true); }}
            variant="outline"
            data-testid="lopd-enlace-casa-button"
          >
            <Link2 className="w-4 h-4 mr-2" /> Enlace para casa
          </Button>
          <Button onClick={() => setOpen(true)} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="lopd-new-button">
            <Plus className="w-4 h-4 mr-2" /> Generar LOPD
          </Button>
        </>}
      />

      {/* Aviso: pacientes pendientes de firma LOPD */}
      {/* No se ha podido saber quién está pendiente. Se dice, en vez de callar:
          el bloque rojo de abajo desaparecería sin más y la pantalla se leería
          como «no hay nadie pendiente», que es lo contrario de lo que se sabe
          (que no se sabe). Con su botón, para no tener que recargar la página. */}
      {falloPendientes && (
        <div className="cjo-card p-4 mb-4 border-l-4 border-l-amber-500 bg-amber-50/40" data-testid="lopd-pending-fallo">
          <div className="text-sm font-semibold text-amber-900">No se ha podido consultar quién está pendiente de firma</div>
          <div className="text-xs text-amber-900 mt-1">
            {falloPendientes}. Esto no quiere decir que no haya ninguno: vuelve a intentarlo.
          </div>
          <Button size="sm" variant="outline" className="mt-2" onClick={cargarPendientes} data-testid="lopd-pending-retry">
            Reintentar
          </Button>
        </div>
      )}

      {pendientes !== null && pendientes.total > 0 && (
        <div className="cjo-card p-4 mb-4 border-l-4 border-l-red-600 bg-red-50/30" data-testid="lopd-pending-block">
          <div className="flex items-start gap-3">
            <div className="bg-red-100 text-red-700 rounded-full p-1.5">
              <ShieldCheck className="w-4 h-4" />
            </div>
            <div className="flex-1">
              <div className="font-semibold text-red-900 mb-1">
                {/* Sin el triángulo de aviso delante, que en los equipos de la
                    clínica salía como un cuadrado (5/10/2026): el bloque ya es
                    rojo y lleva su icono. */}
                {pendientes.total} {pendientes.total === 1 ? 'paciente pendiente' : 'pacientes pendientes'} de firma LOPD
              </div>
              <div className="text-xs text-red-900 mb-2">
                Estos pacientes están dados de alta pero aún no tienen LOPD firmado. Pulsa el nombre para generarlo, el icono de WhatsApp para enviarles el enlace, o usa "Modo tablet" para que firmen en la clínica.
              </div>
              <div className="flex flex-wrap gap-1.5">
                {pastillas.map(p => {
                  const sending = homeLinkBusy === p.id;
                  return (
                    <span key={p.id} className="inline-flex items-stretch rounded border border-red-300 overflow-hidden bg-white">
                      <button
                        onClick={() => window.location.href = `/lopd?patient=${p.id}`}
                        className="text-[11px] text-red-900 px-2 py-1 hover:bg-red-100"
                        data-testid="lopd-pending-patient-pill"
                      >
                        {fullName(p)}{p.nif ? ` · ${p.nif}` : ''}
                      </button>
                      {/* Copiar: para cualquier paciente de la lista, uno a uno —
                          antes solo se podía copiar si había EXACTAMENTE un
                          pendiente (el botón de arriba); con varios, como hoy,
                          no había forma de copiar el enlace de nadie en concreto. */}
                      <button
                        type="button"
                        onClick={() => copyHomeLinkForPatient(p)}
                        disabled={!!homeLinkBusy}
                        title="Copiar enlace seguro"
                        className="inline-flex items-center px-1.5 border-l border-red-300 text-red-900 hover:bg-red-100 disabled:opacity-60"
                        data-testid="lopd-pending-copy"
                      >
                        {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Copy className="w-3.5 h-3.5" />}
                      </button>
                      {p.phone && (
                        <button
                          type="button"
                          onClick={() => sendHomeLinkByWhatsApp(p)}
                          disabled={!!homeLinkBusy}
                          title="Enviar enlace LOPD por WhatsApp"
                          className="inline-flex items-center px-1.5 border-l border-red-300 text-emerald-700 hover:bg-emerald-50 disabled:opacity-60"
                          data-testid="lopd-pending-wa"
                        >
                          {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MessageCircle className="w-3.5 h-3.5" />}
                        </button>
                      )}
                    </span>
                  );
                })}
                {masPendientes > 0 && (
                  <span className="text-[11px] text-red-900 px-1 py-1">+{masPendientes} más</span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Los «Firmar y enviar» que no le salieron al paciente (30/10/2026). */}
      <IntentosQueNoSalieron />

      {/* Buscar una LOPD concreta sin bajar por toda la lista (administración,
          17/9/2026). La busca el servidor entre TODAS, no solo entre las que
          se bajaron. La caja no se esconde porque una búsqueda no encuentre
          nada: si se fuera, no habría manera de borrar lo escrito. */}
      {!primeraCarga && ((total?.n || 0) > 0 || q) && (
        <div className="cjo-card p-4 mb-4">
          <div className="relative max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nombre, apellido o teléfono…"
              aria-label="Buscar LOPD por nombre, apellido o teléfono"
              className="pl-9"
              data-testid="lopd-search-input"
            />
          </div>
          {q && (
            <div className="text-xs text-muted-foreground mt-2" data-testid="lopd-search-count">
              {alDia
                ? `${visibles.length} de ${total?.n ?? 0}${total?.recortado ? '+' : ''} registros${truncado ? ` · se muestran las ${LIMITE} primeras coincidencias` : ''}`
                : 'Buscando en todas las LOPD…'}
            </div>
          )}
        </div>
      )}

      <div className="cjo-card overflow-hidden">
        {/* LO QUE NUNCA PUEDE PASAR: que esto diga que algo no existe cuando
            lo que pasa es que no ha mirado. Solo se afirma «no coincide»
            cuando el servidor ya ha contestado A ESTE TEXTO habiendo buscado
            entre todas; si está buscando, lo dice; si ha fallado, lo dice. */}
        {primeraCarga ? <div className="py-10 text-center text-sm text-muted-foreground">Cargando…</div>
          : fallo ? <EmptyState icon={ShieldCheck} title="No se pudo consultar" description={`${fallo}. Esto no quiere decir que no haya LOPD: vuelve a intentarlo.`} />
          : visibles.length > 0 ? <div className="overflow-x-auto"><table className="w-full text-sm">
              <thead className="bg-[rgba(46,110,94,0.04)] text-left"><tr>
                <th className="py-3 px-4 font-semibold text-[#13231E]">Paciente</th>
                <th className="py-3 px-4 font-semibold text-[#13231E]">Móvil</th>
                <th className="py-3 px-4 font-semibold text-[#13231E]">Firmado</th>
                <th className="py-3 px-4 font-semibold text-[#13231E]">Origen</th>
                <th className="py-3 px-4 font-semibold text-[#13231E]">Recordatorios</th>
                <th className="py-3 px-4 font-semibold text-[#13231E]">Salud</th>
                <th className="py-3 px-4 font-semibold text-[#13231E]">Comercial</th>
                <th></th>
              </tr></thead>
              <tbody>{visibles.map(l => <tr key={l.id} className="border-t border-border hover:bg-[rgba(46,110,94,0.03)]" data-testid="lopd-row">
                <td className="py-3 px-4 font-medium">{l.patient_name}</td>
                <td className="py-3 px-4 text-xs whitespace-nowrap" data-testid="lopd-row-telefono">{telefonoDeLaLopd(l) || '—'}</td>
                <td className="py-3 px-4 text-xs">{formatDate(l.signed_date)}</td>
                <td className="py-3 px-4"><span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium tracking-wide uppercase ${(ORIGEN_LOPD[l.source] || ORIGEN_MANUAL).clase}`}>{(ORIGEN_LOPD[l.source] || ORIGEN_MANUAL).texto}</span></td>
                <td className="py-3 px-4">{l.consent_reminders ? 'Sí' : '—'}</td>
                <td className="py-3 px-4">{l.consent_health_info ? 'Sí' : '—'}</td>
                <td className="py-3 px-4">{l.consent_commercial ? 'Sí' : '—'}</td>
                <td className="py-3 px-4 text-right"><div className="flex gap-1 items-center justify-end">
                  <Button size="sm" variant="outline" onClick={() => setPreview({ open: true, url: `${BACKEND_URL}/api/lopd/${l.id}/pdf`, filename: `lopd_${l.id}.pdf`, title: `LOPD · ${l.patient_name}` })} data-testid="lopd-pdf-button"><Eye className="w-4 h-4" /></Button>
                  {/* Firmada: sin papelera (el servidor no la deja borrar, 23/9/2026); en su sitio, cómo rehacerla. */}
                  {lopdFirmada(l)
                    ? <Button size="sm" variant="ghost" onClick={() => rehacerLopd(l)} title={REHACER_LOPD} aria-label="Firmada: cómo rehacerla" className="text-[#2E6E5E]" data-testid="lopd-rehacer"><Link2 className="w-3.5 h-3.5 mr-1" /> Rehacer</Button>
                    : <Button size="sm" variant="ghost" onClick={() => onDelete(l.id)} title="Eliminar (sin firma del paciente)" aria-label="Eliminar" className="text-destructive" data-testid="lopd-delete-button"><Trash2 className="w-3.5 h-3.5" /></Button>}
                </div></td>
              </tr>)}</tbody></table></div>
          : !q ? <EmptyState icon={ShieldCheck} title="Sin LOPD" description="Genera el primer LOPD o sube una foto del documento firmado." />
          : !alDia ? <EmptyState icon={ShieldCheck} title="Buscando…" description={`Buscando «${q}» entre todas las LOPD.`} />
          : <EmptyState icon={ShieldCheck} title="Sin coincidencias" description={`Ninguna LOPD coincide con «${q}». Se ha buscado en todas, por nombre, apellido, NIF y teléfono.`} />}
      </div>

      <HomeLinkDialog open={homeLinkOpen} onOpenChange={setHomeLinkOpen} paraPaciente={homeLinkPara} />
      <NewLopdDialog open={open} onOpenChange={setOpen} initialPatient={initialPatient} onCreated={refrescar} />
      <ReviewExtractedDialog
        open={reviewOpen}
        onOpenChange={setReviewOpen}
        extracted={extracted}
        onSaved={refrescar}
      />
      <PdfPreviewDialog open={preview.open} onOpenChange={(v) => setPreview(s => ({ ...s, open: v }))} url={preview.url} filename={preview.filename} title={preview.title} />
    </div>
  );
}
